/**
 * Screen-level building blocks shared across features.
 *
 * These sit above @w7/ui because they know about rupees, `lll` dates and this
 * console's semantics. Anything here that turned out to be app-agnostic would
 * belong in the kit instead.
 */

import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Chip, Icon, Sparkline, StateBlock, cx } from '@w7/ui'
import { EMPTY, lll, money, percent, toneOf } from '@/lib/format'
import { parseCurrencyType } from '@/lib/currency'
import { useViewerCurrency } from '@/api/hooks/shared/useViewerCurrency'
import { POLL_INTERVAL_MS } from '@/data/api'
import type { Tone } from '@w7/ui'

/* ----------------------------------------------------------------- money -- */

export function Money({
  value,
  signed = false,
  compact = false,
  tone: forcedTone,
  invert = false,
  className,
  currency,
  alreadyDisplay = false,
  symbol = true,
}: {
  value: number | null | undefined
  signed?: boolean
  compact?: boolean
  /** Override the sign-derived tone. Pass 'neutral' for balances and limits. */
  tone?: Tone
  /** For columns where a negative number is good news. */
  invert?: boolean
  className?: string
  /** Lab currency of this figure. Defaults to the signed-in node. */
  currency?: string
  /** Account credit/balance are already in display units. Books pass false. */
  alreadyDisplay?: boolean
  /** Show ₹ / Pts. Default true. Lab My P/L uses bare indianCurrency figures. */
  symbol?: boolean
}) {
  const viewer = useViewerCurrency()
  const ccy = parseCurrencyType(currency ?? viewer)
  const tone = forcedTone ?? toneOf(value, invert)
  const formatted = money(value, {
    signed,
    currency: ccy,
    alreadyDisplay,
    round: compact,
    symbol,
  })
  return (
    <span
      className={cx('w7-mono', `w7-tone-${tone}`, className)}
      title={compact ? formatted : undefined}
    >
      {formatted}
    </span>
  )
}

/** A neutral money figure — balances, limits, turnover. Never green or red. */
export function Amount({
  value,
  compact = false,
  currency,
  alreadyDisplay = false,
}: {
  value: number | null | undefined
  compact?: boolean
  currency?: string
  alreadyDisplay?: boolean
}) {
  return (
    <Money
      value={value}
      tone="neutral"
      compact={compact}
      currency={currency}
      alreadyDisplay={alreadyDisplay}
    />
  )
}

/* ------------------------------------------------------------ provenance -- */

/**
 * Where a number came from and how fresh it is.
 *
 * Give/take balances refresh over the credit SSE stream when Live. Other
 * surfaces that still poll must say so (exposure journey).
 */
export function AsOf({
  at,
  polls = false,
  stream = false,
}: {
  at?: string | number | null
  polls?: boolean
  /** Live credit stream connected — balances update on give/take events. */
  stream?: boolean
}) {
  return (
    <span className="w7-asof">
      <Icon name="clock" size={12} />
      As of {at ? lll(at) : EMPTY}
      {stream
        ? ' · live credit stream'
        : polls
          ? ` · refreshes every ${Math.round(POLL_INTERVAL_MS / 1000)}s`
          : null}
    </span>
  )
}

/**
 * The banner an exposure surface shows instead of a number when the projection
 * has fallen behind. Never render a stale figure — pre-bet checks are failing
 * conservatively while it is stale, so a number here would be actively wrong.
 */
export function StaleProjection({ asOf }: { asOf?: string }) {
  return (
    <StateBlock icon="warning" title="Exposure is unavailable right now" tone="error">
      The risk projection has fallen behind{asOf ? ` (last advanced ${lll(asOf)})` : ''}. Figures are
      hidden rather than shown stale — bet acceptance is failing conservatively until it catches up.
    </StateBlock>
  )
}

/* ------------------------------------------------------------- kpi extras -- */

/** Period-over-period change, formatted for a KPI tile's `delta`. */
export function deltaOf(current: number, previous: number): { text: string; tone: Tone } | null {
  if (!Number.isFinite(current) || !Number.isFinite(previous) || previous === 0) return null
  const change = (current - previous) / Math.abs(previous)
  if (!Number.isFinite(change)) return null
  return {
    text: `${change >= 0 ? '+' : ''}${percent(change, 0)}`,
    tone: change === 0 ? 'neutral' : change > 0 ? 'profit' : 'loss',
  }
}

export function MiniSpark({ values, invert = false }: { values: number[]; invert?: boolean }) {
  const first = values[0] ?? 0
  const last = values[values.length - 1] ?? 0
  const direction = invert ? first - last : last - first
  return (
    <Sparkline
      values={values}
      width={64}
      height={22}
      color={direction > 0 ? 'var(--w7-profit)' : direction < 0 ? 'var(--w7-loss)' : 'var(--w7-text-3)'}
    />
  )
}

/* ------------------------------------------------------------------ misc -- */

export function SectionGrid({
  columns = 2,
  children,
}: {
  columns?: 1 | 2 | 3 | 4
  children: ReactNode
}) {
  return <div className={`w7-grid w7-grid-${columns}`}>{children}</div>
}

export function AccountLink({ id, username }: { id: string; username: string }) {
  return (
    <Link to={`/member/${id}`} className="w7-link">
      {username}
    </Link>
  )
}

/** Attention badges — operational signals only, never fraud/surveillance. */
export function FlagChips({ flags }: { flags: string[] }) {
  if (flags.length === 0) return null
  const map: Record<string, { label: string; tone: Tone }> = {
    'exposure-over-limit': { label: 'Over limit', tone: 'loss' },
    'exposure-near-limit': { label: 'Near limit', tone: 'warn' },
    'dormant-funded': { label: 'Dormant, funded', tone: 'warn' },
    'settlement-overdue': { label: 'Settlement due', tone: 'warn' },
    'never-logged-in': { label: 'Never signed in', tone: 'muted' },
    'no-login-yet': { label: 'No login yet', tone: 'info' },
  }
  return (
    <span className="w7-row" style={{ gap: 4 }}>
      {flags.map((flag) => {
        const entry = map[flag]
        if (!entry) return null
        return (
          <Chip key={flag} tone={entry.tone} size="sm">
            {entry.label}
          </Chip>
        )
      })}
    </span>
  )
}

/** Download the current view. Every report screen offers it in the same place. */
export function exportCsv(filename: string, rows: Record<string, unknown>[]) {
  if (rows.length === 0) return
  const headers = Object.keys(rows[0]!)
  const escape = (value: unknown) => {
    const text = value === null || value === undefined ? '' : String(value)
    return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
  }
  const csv = [headers.join(','), ...rows.map((row) => headers.map((h) => escape(row[h])).join(','))].join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}
